import { useDispatch, useSelector } from 'react-redux'
import { LinkedInProfileResponse } from '../../api/twin'
import type { Faq } from '../../pages/TwinGenerationPage/components/FaqTab/FaqTab'
import type { Inputs } from '../../pages/TwinGenerationPage/components/ProfileTab/ProfileTab'

import type { IKnowledgeFile } from '../../store/reducers/twinReducer'
import {
  addFaq,
  addKnowledgeFile,
  clearAllErrors,
  clearTabError,
  deleteFaq,
  getTwinState,
  removeKnowledgeFile,
  resetTwinState,
  setAudioUrl,
  setFaqs,
  setInterviewSessionId,
  setIsCameraReady,
  setKnowledge,
  setLinkedInProfile,
  setPreviewOriginalUrl,
  setPreviewTabName,
  setPreviewUrl,
  setSelectedImageStyle,
  setStyledOriginUrls,
  setTabError,
  setTwinId,
  setUploadedKnowledge,
  setUserData,
  updateFaq,
  updateKnowledgeFileStatus,
} from '../../store/reducers/twinReducer'
import { AppDispatch } from '../../store/store'

export const useTwinPreview = () => {
  const state = useSelector(getTwinState)
  const dispatch: AppDispatch = useDispatch()

  return {
    previewUrl: state.previewUrl,
    styledOriginUrls: state.styledOriginUrls,
    selectedImageStyle: state.selectedImageStyle,
    linkedInImageUrl: state.linkedInImageUrl,
    previewTabName: state.previewTabName,
    previewOriginalUrl: state.previewOriginalUrl,
    setPreviewUrl: (url: string) => dispatch(setPreviewUrl(url)),
    setStyledOriginUrls: (urls: Record<string, string>) =>
      dispatch(setStyledOriginUrls(urls)),
    setSelectedImageStyle: (imageStyle: string | null) =>
      dispatch(setSelectedImageStyle(imageStyle)),
    setPreviewTabName: (name: string) => dispatch(setPreviewTabName(name)),
    setPreviewOriginalUrl: (url: string | null) =>
      dispatch(setPreviewOriginalUrl(url)),
  }
}

export const useTwinData = () => {
  const state = useSelector(getTwinState)
  const dispatch: AppDispatch = useDispatch()

  return {
    isCameraReady: state.isCameraReady,
    previewUrl: state.previewUrl,
    previewOriginalUrl: state.previewOriginalUrl,
    profileJson: state.profileJson,
    linkedInSummary: state.linkedInSummary,
    audioUrl: state.audioUrl,
    knowledge: state.knowledge,
    jobTitle: state.jobTitle,
    firstName: state.firstName,
    lastName: state.lastName,
    title: state.title,
    twinId: state.twinId,
    interviewSessionId: state.interviewSessionId,
    faqs: state.faqs,
    uploadedKnowledge: state.uploadedKnowledge,
    resetTwinState: () => dispatch(resetTwinState()),
    setLinkedInProfile: (profile: LinkedInProfileResponse) =>
      dispatch(setLinkedInProfile(profile)),
    setAudioUrl: (url: string) => dispatch(setAudioUrl(url)),
    setKnowledge: (knowledge: Record<string, string>) =>
      dispatch(setKnowledge(knowledge)),
    setUserData: (data: Inputs) => dispatch(setUserData(data)),
    setTwinId: (id: string | null) => dispatch(setTwinId(id)),
    setIsCameraReady: (isReady: boolean) => dispatch(setIsCameraReady(isReady)),
    setInterviewSessionId: (id: string | null) =>
      dispatch(setInterviewSessionId(id)),
    setFaqs: (faqs: Faq[]) => dispatch(setFaqs(faqs)),
    addFaq: (faq: Faq) => dispatch(addFaq(faq)),
    updateFaq: (faq: Faq) => dispatch(updateFaq(faq)),
    deleteFaq: (id: string) => dispatch(deleteFaq(id)),
    addKnowledgeFile: (file: IKnowledgeFile) =>
      dispatch(addKnowledgeFile(file)),
    updateKnowledgeFileStatus: (payload: {
      fileName: string
      status: IKnowledgeFile['status']
      errorMessage?: string
      file: File | null
      uploadUrl?: string
      id: string
      uploadId?: string
    }) => dispatch(updateKnowledgeFileStatus(payload)),
    removeKnowledgeFile: (fileName: string) =>
      dispatch(removeKnowledgeFile(fileName)),
    setUploadedKnowledge: (files: Record<string, IKnowledgeFile>) =>
      dispatch(setUploadedKnowledge(files)),
  }
}

export const useTwinErrors = () => {
  const state = useSelector(getTwinState)
  const dispatch: AppDispatch = useDispatch()

  return {
    errors: state.errors,
    setTabError: (tab: string, error: string) =>
      dispatch(setTabError({ tab, error })),
    clearTabError: (tab: string) => dispatch(clearTabError(tab)),
    clearAllErrors: () => dispatch(clearAllErrors()),
  }
}
